// StreakStripView — the "Streak" view for cadence streaks (weekly / monthly
// events). Where the daily CalendarSummaryView is a 2-D month heatmap, this is
// its 1-D analogue: a horizontal track with ONE cell per cadence bucket (week or
// month), hit vs missed, with the CURRENT run (green) and LONGEST/record run
// (gold) lit up — the exact same mental model and the same Current-vs-Longest
// banner, just a coarser cell. Used when a daily grid would be nearly empty and
// peg every run at 1.
//
// Buckets are fixed calendar periods: weeks are SUNDAY-anchored (US convention,
// matching the backend's `_bucket_index`; JS `getDay()` is already Sunday=0),
// months are calendar months. A bucket is "hit" if ≥1 check-in fell in it; one
// empty bucket breaks a run. The single trailing bucket that contains *today* is
// special: if it has no check-in yet it renders as "pending" (a dashed outline),
// NOT a miss — an in-progress week/month hasn't been skipped until it's over.
// Past that one bucket of slack the run is over: a strip whose last hit is two
// or more buckets back has NO current run (0 in the banner, no green cells),
// rather than reporting the stale run's length forever.
//
// Like CalendarSummaryView this computes its runs on the frontend, windowed to
// the selected period, so the strip, the banner, and the Stats tab's
// backend-computed longest/current all agree.

import React, { useEffect, useMemo } from 'react'
import { motion } from 'motion/react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useHorizontalOverflow } from '../../../hooks/useHorizontalOverflow'
import { isoDay } from '../../../charts/bodies/boolAggregation'
import type { DateWindow } from '../../../charts/windowRange'
import type { SummaryViewBodyProps } from '../viewRegistry'
import {
  BEAR_ASPECT,
  HoneyBear,
  StreakBanner,
  CoverageBanner,
} from './streakCommon'
import type { BearState } from './streakCommon'

export type StripCadence = 'weekly' | 'monthly'

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const pad2 = (n: number) => String(n).padStart(2, '0')

function dayStartMs(ms: number): number {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}
/** Local-midnight ms of the Sunday that starts the week containing `ms`. */
function weekStartMs(ms: number): number {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay()).getTime()
}
function monthStartMs(ms: number): number {
  const d = new Date(ms)
  return new Date(d.getFullYear(), d.getMonth(), 1).getTime()
}
function bucketStartMs(ms: number, cadence: StripCadence): number {
  return cadence === 'weekly' ? weekStartMs(ms) : monthStartMs(ms)
}
function nextBucketMs(ms: number, cadence: StripCadence): number {
  const d = new Date(ms)
  return cadence === 'weekly'
    ? new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7).getTime()
    : new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime()
}
function bucketKey(ms: number, cadence: StripCadence): string {
  const d = new Date(bucketStartMs(ms, cadence))
  return cadence === 'weekly'
    ? `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
    : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`
}

interface StripCell {
  key: string
  startMs: number
  hit: boolean
  /** The in-progress trailing bucket (contains today) with no check-in yet. */
  pending: boolean
  inCurrent: boolean
  inLongest: boolean
  /** Month abbrev shown under the strip at a month boundary (weekly) or on
   *  every cell (monthly). */
  monthLabel?: string
  /** Two-digit year shown alongside `monthLabel` when the year changes. */
  yearLabel?: string
  tooltip: string
}

export interface StripResult {
  cells: StripCell[]
  current: number
  longest: number
}

/** Bucket the active dates into hit/missed cells across the selected window and
 *  resolve the current (latest) and longest consecutive-hit runs. Pure, so the
 *  view and its tests share one definition; `todayMs` is injected for both the
 *  pending-bucket rule and to cap the strip at the present (no future cells). */
export function computeStrip(
  activeDates: number[],
  window: DateWindow,
  cadence: StripCadence,
  todayMs: number,
  spanDates: number[] = activeDates,
): StripResult {
  const finite = Number.isFinite(window.start) && Number.isFinite(window.end)
  const inScope = finite
    ? activeDates.filter((ms) => ms >= window.start && ms < window.end)
    : activeDates

  let firstMs: number
  let lastMs: number
  if (finite) {
    firstMs = bucketStartMs(window.start, cadence)
    // Last day in the window is window.end-1; never draw past today's bucket.
    const lastDay = Math.min(window.end - 1, dayStartMs(todayMs))
    if (lastDay < firstMs) return { cells: [], current: 0, longest: 0 }
    lastMs = bucketStartMs(lastDay, cadence)
  } else {
    // Unbounded: from the first day with data, counted or not (`spanDates`).
    const withData = [...spanDates, ...activeDates]
    if (withData.length === 0) return { cells: [], current: 0, longest: 0 }
    firstMs = bucketStartMs(Math.min(...withData), cadence)
    lastMs = bucketStartMs(Math.max(Math.max(...withData), todayMs), cadence)
  }

  const hits = new Set(inScope.map((ms) => bucketKey(ms, cadence)))
  const todayKey = bucketKey(todayMs, cadence)

  const raw: Array<{ key: string; startMs: number; hit: boolean }> = []
  for (let ms = firstMs; ms <= lastMs; ms = nextBucketMs(ms, cadence)) {
    const key = bucketKey(ms, cadence)
    raw.push({ key, startMs: ms, hit: hits.has(key) })
    if (raw.length > 1200) break // runaway guard (years of weeks)
  }

  // Runs of consecutive hit cells. Pending (non-hit) cells can't extend a run;
  // since pending is always the trailing cell after the last hit, it never sits
  // inside a run, so treating it as a non-hit here is correct.
  const runs: number[][] = []
  let run: number[] = []
  raw.forEach((c, i) => {
    if (c.hit) {
      run.push(i)
    } else if (run.length) {
      runs.push(run)
      run = []
    }
  })
  if (run.length) runs.push(run)

  // The latest run only counts as CURRENT while it's still alive: its last
  // bucket has to be the reference bucket or the one before it. The reference is
  // the strip's last cell — `lastMs` above is already the period's final bucket
  // clamped to today — and the one bucket of slack is that in-progress bucket,
  // which hasn't been skipped until it's over. A run that ended earlier is
  // broken, so the banner reads 0 and the cells drop the green highlight (they
  // stay gold if they're the record). Mirrors the backend's `_streak_lengths`.
  const latestRun = runs.length ? runs[runs.length - 1] : []
  const alive = latestRun.length > 0 && latestRun[latestRun.length - 1] >= raw.length - 2
  const currentRun = alive ? latestRun : []
  // `>=` so a tie resolves to the latest run (a current run matching the record
  // reads as the record), mirroring CalendarSummaryView.
  let longestRun: number[] = []
  for (const r of runs) if (r.length >= longestRun.length) longestRun = r
  const inCurrent = new Set(currentRun)
  const inLongest = new Set(longestRun)

  let prevMonth = -1
  let prevYear = -1
  const cells: StripCell[] = raw.map((c, i) => {
    const d = new Date(c.startMs)
    const month = d.getMonth()
    const year = d.getFullYear()
    const isLongest = inLongest.has(i)
    const isCurrent = inCurrent.has(i)
    const pending = !c.hit && c.key === todayKey

    let monthLabel: string | undefined
    let yearLabel: string | undefined
    if (cadence === 'monthly') {
      monthLabel = MONTH_NAMES[month]
      if (year !== prevYear) yearLabel = `'${String(year).slice(-2)}`
    } else if (month !== prevMonth || year !== prevYear) {
      monthLabel = MONTH_NAMES[month]
      if (year !== prevYear) yearLabel = `'${String(year).slice(-2)}`
    }
    prevMonth = month
    prevYear = year

    const dateLabel =
      cadence === 'weekly'
        ? `Week of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : `${MONTH_NAMES[month]} ${year}`
    const status = isCurrent
      ? isLongest
        ? 'current & longest streak'
        : 'current streak'
      : isLongest
        ? 'longest streak'
        : pending
          ? 'in progress'
          : c.hit
            ? 'logged'
            : 'missed'

    return {
      key: c.key,
      startMs: c.startMs,
      hit: c.hit,
      pending,
      inCurrent: isCurrent,
      inLongest: isLongest,
      monthLabel,
      yearLabel,
      tooltip: `${dateLabel}: ${status}`,
    }
  })

  return { cells, current: currentRun.length, longest: longestRun.length }
}

/** What a bucket shows, for its `data-state` test hook: the calendar's
 *  vocabulary (current / longest / logged / empty) plus `pending` for the
 *  in-progress bucket. A coverage strip is only logged, empty or pending. */
function cellState(c: StripCell, coverage: boolean): string {
  if (c.pending) return 'pending'
  if (!c.hit) return 'empty'
  if (coverage) return 'logged'
  if (c.inCurrent) return 'current'
  if (c.inLongest) return 'longest'
  return 'logged'
}

/** Which bottle a bucket gets. A logged bucket is a bear filled with its run's
 *  color; a missed one is an empty bottle. The in-progress trailing bucket gets
 *  the empty bottle too — it hasn't been skipped yet, so the strip marks it
 *  with a dashed rule beneath rather than by altering the artwork. */
function cellBear(c: StripCell, coverage: boolean): BearState {
  if (c.pending || !c.hit) return 'empty'
  // logging_rate coverage: a plain logged vs missed backdrop, no current or
  // longest run highlight.
  if (coverage) return 'green'
  if (c.inCurrent) return 'green'
  if (c.inLongest) return 'gold'
  return 'neutral'
}

const StreakStripView: React.FC<SummaryViewBodyProps> = ({
  config,
  numeric,
  window,
  activeDates = [],
  pagingDates = activeDates,
}) => {
  const cadence: StripCadence =
    (config?.params as { cadence?: string } | null | undefined)?.cadence === 'monthly'
      ? 'monthly'
      : 'weekly'
  // logging_rate reuses this strip as a coverage visual (see cellFill / banner).
  const coverage = config?.kind === 'logging_rate'
  const unitLabel = cadence === 'weekly' ? 'wks' : 'mos'
  // The bottles carry a face, arms and a cap, none of which survived the old
  // bars' 40px row: the eyes land under a pixel. At 56 they read, and the
  // silhouette is narrow enough (BEAR_ASPECT) that a bottle is only ~28px
  // wide, so the weekly column stays modest even though it's wider than the
  // 14px bar it replaces.
  //
  // The column is exactly the bottle's width and all the breathing room lives
  // in the flex `gap`, so a bottle's left edge, its column's left edge and its
  // month tick's left edge are the same x. Padding the column instead and
  // centering the bottle inside it puts the artwork half the padding to the
  // right of the tick that labels it. Months are sparser, so they get a wider
  // gap rather than a wider column.
  const BEAR_H = 56
  const cellW = Math.round(BEAR_H * BEAR_ASPECT)
  const cellGap = cadence === 'weekly' ? 6 : 18

  const { cells, current, longest } = useMemo(
    () => computeStrip(activeDates, window, cadence, Date.now(), pagingDates),
    [activeDates, window, cadence, pagingDates],
  )

  // Horizontal scrolling mirrors CalendarSummaryView, through the same hook: the
  // scrollbar is hidden (it crowds the small cells), so chevrons page the strip
  // and the hook tracks which ends it's parked against for their disabled state.
  const { ref: scrollCallbackRef, scrollRef, overflow, atStart, atEnd, sync: syncScroll } =
    useHorizontalOverflow<HTMLDivElement>()

  // Open scrolled to the most recent (right-most) bucket.
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollLeft = el.scrollWidth
    syncScroll()
  }, [cells, scrollRef, syncScroll])

  const pageBy = (dir: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.8), behavior: 'smooth' })
  }

  if (cells.length === 0) {
    return (
      <div data-testid="summary-streak-strip" data-cadence={cadence} data-buckets={0}>
        <div className="flex h-48 items-center justify-center text-sm text-gray-500">
          <p>No check-ins yet.</p>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="summary-streak-strip"
      data-cadence={cadence}
      data-buckets={cells.length}
      data-current={coverage ? undefined : current}
      data-longest={coverage ? undefined : longest}
    >
      {coverage ? (
        <CoverageBanner
          pct={Number(numeric?.numeric_payload?.pct ?? 0)}
          active={Number(numeric?.numeric_payload?.active_buckets ?? 0)}
          total={Number(numeric?.numeric_payload?.total_buckets ?? 0)}
          unitLabel={unitLabel}
        />
      ) : (
        <StreakBanner current={current} longest={longest} unitLabel={unitLabel} />
      )}

      <div
        ref={scrollCallbackRef}
        className="overflow-x-auto py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex" style={{ gap: cellGap }}>
          {cells.map((c) => (
            <div
              key={c.key}
              className="flex flex-shrink-0 flex-col items-stretch"
              style={{ width: cellW }}
            >
              <div
                title={c.tooltip}
                className={`flex items-center transition duration-150 hover:scale-110 ${
                  c.pending ? 'border-b-2 border-dashed border-gray-400' : ''
                }`}
                style={{ height: BEAR_H }}
                data-day={isoDay(new Date(c.startMs))}
                data-state={cellState(c, coverage)}
                data-hit={c.hit ? '1' : '0'}
                data-pending={c.pending ? '1' : undefined}
                data-bear={cellBear(c, coverage)}
              >
                <HoneyBear height={BEAR_H} state={cellBear(c, coverage)} />
              </div>
              {/* Month tick, with the year stacked on a second line (so a wide
                  "May '25" doesn't overflow its thin column into the next
                  month's tick). Left-aligned and allowed to overflow. */}
              <div className="mt-1 h-6 self-start whitespace-nowrap text-[9px] leading-tight text-gray-400">
                <div>{c.monthLabel}</div>
                {c.yearLabel ? <div>{c.yearLabel}</div> : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Paging arrows — the hidden scrollbar's affordance. */}
      {overflow && (
        <div className="mt-1.5 flex items-center justify-center gap-12">
          {([['prev', -1, ChevronLeft, atStart], ['next', 1, ChevronRight, atEnd]] as const).map(
            ([key, dir, Icon, disabled]) => (
              <motion.button
                key={key}
                type="button"
                aria-label={key === 'prev' ? 'Scroll to earlier buckets' : 'Scroll to later buckets'}
                onClick={() => pageBy(dir)}
                disabled={disabled}
                whileHover={disabled ? undefined : { scale: 1.1 }}
                whileTap={disabled ? undefined : { scale: 0.9 }}
                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                className="rounded-full p-0.5 text-gray-400 hover:text-gray-600 disabled:cursor-default disabled:opacity-25"
              >
                <Icon className="h-4 w-4" />
              </motion.button>
            ),
          )}
        </div>
      )}
    </div>
  )
}

export default StreakStripView
